// Phase 9 (subset) — Patient Risk card.
//
// Drops onto PatientDetail to show the cached adherence-risk score and
// the top contributing factors.  Admin / pharmacist can force-recompute.

import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.jsx';

const TIER_STYLE = {
  high: { bar: '#dc2626', bg: '#fef2f2', text: '#991b1b', label: 'High risk' },
  med:  { bar: '#d97706', bg: '#fffbeb', text: '#92400e', label: 'Medium risk' },
  low:  { bar: '#059669', bg: '#f0fdf4', text: '#065f46', label: 'Low risk' },
};

// 22 is the current max with the 3-factor subset.  When more factors are
// wired this should bump toward 100; keeping it as a const makes the gauge
// trivial to re-tune.
const SCORE_MAX = 22;

export default function RiskCard({ patientId }) {
  const { user } = useAuth();
  const canRecompute = user?.role === 'admin' || user?.role === 'pharmacist';

  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr]     = useState(null);
  const [busy, setBusy]   = useState(false);

  async function load() {
    setLoading(true); setErr(null);
    try { setData(await api.patientRisk(patientId)); }
    catch (e) { setErr(e.message || 'failed to load risk'); }
    finally { setLoading(false); }
  }

  async function recompute() {
    setBusy(true); setErr(null);
    try { setData(await api.recomputeRisk(patientId)); }
    catch (e) { setErr(e.message || 'recompute failed'); }
    finally { setBusy(false); }
  }

  useEffect(() => { if (patientId) load(); /* eslint-disable-next-line */ }, [patientId]);

  if (loading) {
    return <div className="bg-white rounded-lg border border-slate-200 p-4 text-sm text-slate-500">Loading risk…</div>;
  }
  if (err) {
    return <div className="bg-red-50 border border-red-200 p-3 rounded text-sm text-red-800">{err}</div>;
  }
  if (!data) return null;

  const tierKey = data.tier || 'low';
  const style   = TIER_STYLE[tierKey] || TIER_STYLE.low;
  const score   = data.score || 0;
  const pct     = Math.min(100, Math.round((score / SCORE_MAX) * 100));
  const top     = Array.isArray(data.top_factors) ? data.top_factors : [];

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Adherence risk</div>
          <div className="flex items-baseline gap-2 mt-0.5">
            <div className="text-2xl font-bold">{score}<span className="text-sm font-normal text-slate-400"> / {SCORE_MAX}</span></div>
            <span className="px-2 py-0.5 rounded text-xs font-medium border"
                  style={{ background: style.bg, color: style.text, borderColor: style.bar + '40' }}>
              {style.label}
            </span>
          </div>
        </div>
        {canRecompute && (
          <button className="btn-ghost text-xs" disabled={busy} onClick={recompute}>
            {busy ? '…' : 'Recompute'}
          </button>
        )}
      </div>

      {/* Score bar */}
      <div className="h-2 rounded bg-slate-100 overflow-hidden mb-3">
        <div className="h-full rounded transition-all"
             style={{ width: `${pct}%`, background: style.bar }} />
      </div>

      {top.length === 0 ? (
        <div className="text-sm text-slate-500">No risk factors detected.</div>
      ) : (
        <ul className="space-y-1.5">
          {top.map(f => (
            <li key={f.id} className="flex items-start justify-between text-sm gap-3">
              <div>
                <div className="text-slate-800">{f.label}</div>
                {f.meta?.classes && f.meta.classes.length > 0 && (
                  <div className="text-xs text-slate-500 mt-0.5">
                    Classes: {f.meta.classes.join(', ')}
                  </div>
                )}
                {f.meta?.age && (
                  <div className="text-xs text-slate-500 mt-0.5">Age: {f.meta.age}</div>
                )}
                {f.meta?.active_med_count && (
                  <div className="text-xs text-slate-500 mt-0.5">
                    {f.meta.active_med_count} active meds
                  </div>
                )}
              </div>
              <span className="text-xs font-mono text-slate-500 shrink-0">+{f.pts}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="text-xs text-slate-400 mt-3 pt-2 border-t border-slate-100">
        3-factor subset · advisory only
      </div>
    </div>
  );
}
